import React, { useEffect, useState, useId } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Car, Store, Shield, ArrowRight, Layers, Loader2, UtensilsCrossed, ImageIcon, CheckCircle2, XCircle, MessageCircle, Eye, EyeOff, Clock, AlertCircle, Inbox, Check, X, FileText, BarChart3, Receipt, LogOut, UserIcon, ChefHat, ShieldCheck } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/ui/Modal';
import { AnimatePresence, motion } from 'framer-motion';
import { useIdleLogout } from '../../lib/useIdleLogout';
interface RestaurantApplication {
  id: string;
  owner_name: string;
  restaurant_name: string;
  whatsapp: string;
  social_handle: string | null;
  business_type: string;
  uses_whatsapp_orders: boolean;
  has_digital_menu: boolean;
  main_goal: string | null;
  status: 'pending' | 'contacted' | 'approved' | 'rejected';
  created_at: string;
}
const ENROLLMENT_OPTIONS = [{
  title: 'Conductor',
  description: 'Registrar conductor, crear o reutilizar vehículo y dejar accesos listos',
  route: '/admin/drivers/new',
  icon: Car,
  accent: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  iconBg: 'bg-blue-500/15',
  btnClass: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
}, {
  title: 'Subir restaurante',
  description: 'Registrar restaurante, crear acceso inicial y dejarlo listo para operar',
  route: '/admin/enrollment/restaurant',
  icon: Store,
  accent: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  iconBg: 'bg-purple-500/15',
  btnClass: 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
}, {
  title: 'Generar Recibo',
  description: 'Crear recibos de pago profesionales para clientes, imprimir o compartir por WhatsApp',
  route: '/admin/enrollment/receipts',
  icon: Receipt,
  accent: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  iconBg: 'bg-emerald-500/15',
  btnClass: 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
}, {
  title: 'Admin de NOIZA',
  description: 'Crear usuario interno de NOIZA y asignar permisos de plataforma',
  route: '/admin/enrollment/platform-admin',
  icon: Shield,
  accent: 'bg-[#006847]/10 border-[#006847]/20 text-[#00a870]',
  iconBg: 'bg-[#006847]/20',
  btnClass: 'bg-[#006847]/20 text-[#00a870] hover:bg-[#006847]/30'
}, {
  title: 'Admin de Restaurante',
  description: 'Asignar usuario existente como administrador de KDS para un restaurante.',
  route: '/admin/enrollment/restaurant-admin',
  icon: ShieldCheck,
  accent: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400',
  iconBg: 'bg-indigo-500/15',
  btnClass: 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30',
  btnLabel: 'Asignar admin'
}, {
  title: 'Actualizar menú',
  description: 'Agregar categorías, productos u opciones a un restaurante existente sin recrearlo',
  route: '/admin/enrollment/menu-update',
  icon: UtensilsCrossed,
  accent: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  iconBg: 'bg-amber-500/15',
  btnClass: 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30',
  btnLabel: 'Iniciar actualización'
}, {
  title: 'Subir imágenes del menú',
  description: 'Relaciona imágenes con productos del menú usando el nombre del archivo',
  route: '/admin/enrollment/menu-images',
  icon: ImageIcon,
  accent: 'bg-teal-500/10 border-teal-500/20 text-teal-400',
  iconBg: 'bg-teal-500/15',
  btnClass: 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30',
  btnLabel: 'Abrir módulo'
}, {
  title: 'Métricas NOIZA',
  description: 'Revisa métricas globales, crecimiento y operación de NOIZA',
  route: '/admin/enrollment/metrics',
  icon: BarChart3,
  accent: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
  iconBg: 'bg-rose-500/15',
  btnClass: 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30',
  btnLabel: 'Ver métricas'
}];
export function EnrollmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // --- Access Guard State ---
  const [guardState, setGuardState] = useState<'loading' | 'authorized' | 'redirecting'>('loading');
  // --- Idle Logout ---
  const {
    showWarning: showIdleWarning
  } = useIdleLogout({
    enabled: guardState === 'authorized',
    timeoutMs: 900000,
    warningBeforeMs: 60000,
    redirectTo: '/admin/login'
  });
  // --- Active Restaurants State ---
  const [restaurants, setRestaurants] = useState<{
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    show_in_marketplace: boolean;
  }[]>([]);
  const [restLoading, setRestLoading] = useState(true);
  const [togglingVisibilityId, setTogglingVisibilityId] = useState<string | null>(null);
  // --- Applications State ---
  const [applications, setApplications] = useState<RestaurantApplication[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'contacted' | 'approved' | 'rejected'>('all');
  const [selectedApp, setSelectedApp] = useState<RestaurantApplication | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [appsModalOpen, setAppsModalOpen] = useState(false);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [showSessionInfo, setShowSessionInfo] = useState(false);
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', {
      replace: true
    });
  };
  const fetchApplications = async () => {
    setAppsLoading(true);
    setAppsError(null);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurant_applications').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setApplications(data || []);
    } catch (err: any) {
      console.error('Error fetching applications:', err);
      setAppsError(err.message);
    } finally {
      setAppsLoading(false);
    }
  };
  // ─── Access Guard ─────────────────────────────────────────────
  useEffect(() => {
    async function guard() {
      try {
        // 1. Check authenticated user
        const {
          data: {
            user
          },
          error: userError
        } = await supabase.auth.getUser();
        if (userError || !user) {
          console.log('[EnrollmentPage] No authenticated user — redirecting to /admin/login');
          setGuardState('redirecting');
          navigate('/admin/login', {
            state: {
              from: location.pathname
            },
            replace: true
          });
          return;
        }
        // 2. Check platform_admins table first (primary source)
        let accessRole: string | null = null;
        const {
          data: platformData,
          error: platformError
        } = await supabase.from('platform_admins').select('role').eq('user_id', user.id).eq('is_active', true).maybeSingle();
        if (!platformError && platformData && (platformData.role === 'super_admin' || platformData.role === 'platform_admin')) {
          accessRole = platformData.role;
        }
        // 3. Fallback: check restaurant_users table
        if (!accessRole) {
          const {
            data: roleData,
            error: roleError
          } = await supabase.from('restaurant_users').select('role').eq('user_id', user.id).eq('is_active', true).in('role', ['super_admin', 'platform_admin']).limit(1).maybeSingle();
          if (roleError) {
            console.error('[EnrollmentPage] Role check error:', roleError);
          }
          if (roleData) {
            accessRole = roleData.role;
          }
        }
        if (!accessRole) {
          console.log('[EnrollmentPage] User lacks required role — redirecting to /');
          setGuardState('redirecting');
          navigate('/', {
            replace: true
          });
          return;
        }
        console.log('[EnrollmentPage] Access granted:', {
          email: user.email,
          role: accessRole
        });
        setSessionEmail(user.email || null);
        setSessionUserId(user.id);
        setGuardState('authorized');
      } catch (err) {
        console.error('[EnrollmentPage] Guard error:', err);
        setGuardState('redirecting');
        navigate('/admin/login', {
          replace: true
        });
      }
    }
    guard();
  }, [navigate, location.pathname]);
  const fetchRestaurants = async () => {
    setRestLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurants').select('id, name, slug, logo_url, show_in_marketplace').eq('is_active', true).order('name', {
        ascending: true
      });
      if (error) throw error;
      setRestaurants(data || []);
    } catch (err: any) {
      console.error('Error fetching restaurants:', err);
    } finally {
      setRestLoading(false);
    }
  };
  const toggleMarketplaceVisibility = async (restaurantId: string, currentValue: boolean) => {
    setTogglingVisibilityId(restaurantId);
    const newValue = !currentValue;
    // Optimistic update
    setRestaurants(prev => prev.map(r => r.id === restaurantId ? {
      ...r,
      show_in_marketplace: newValue
    } : r));
    try {
      const {
        data,
        error
      } = await supabase.from('restaurants').update({
        show_in_marketplace: newValue
      }).eq('id', restaurantId).select('id, show_in_marketplace');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('La actualización fue bloqueada por RLS. Corré FIX_RLS_RESTAURANTS_MARKETPLACE_TOGGLE.sql en Supabase.');
      }
      setToast({
        message: newValue ? 'Restaurante visible en el marketplace' : 'Restaurante oculto del marketplace',
        type: 'success'
      });
    } catch (err: any) {
      console.error('Error toggling marketplace visibility:', err);
      // Revert on error
      setRestaurants(prev => prev.map(r => r.id === restaurantId ? {
        ...r,
        show_in_marketplace: currentValue
      } : r));
      setToast({
        message: 'No se pudo actualizar la visibilidad',
        type: 'error'
      });
    } finally {
      setTogglingVisibilityId(null);
      setTimeout(() => setToast(null), 3000);
    }
  };
  useEffect(() => {
    if (guardState === 'authorized') {
      fetchApplications();
      fetchRestaurants();
    }
  }, [guardState]);
  const updateStatus = async (id: string, newStatus: 'pending' | 'contacted' | 'approved' | 'rejected') => {
    setUpdatingId(id);
    try {
      const {
        error
      } = await supabase.from('restaurant_applications').update({
        status: newStatus
      }).eq('id', id);
      if (error) throw error;
      setToast({
        message: 'Estado actualizado correctamente',
        type: 'success'
      });
      await fetchApplications();
      if (selectedApp && selectedApp.id === id) {
        setSelectedApp({
          ...selectedApp,
          status: newStatus
        });
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      setToast({
        message: 'Error al actualizar estado',
        type: 'error'
      });
    } finally {
      setUpdatingId(null);
      setTimeout(() => setToast(null), 3000);
    }
  };
  const handleWhatsApp = (whatsapp: string) => {
    const cleanNumber = whatsapp.replace(/\D/g, '');
    if (cleanNumber) {
      window.open(`https://wa.me/${cleanNumber}`, '_blank');
    }
  };
  const filteredApps = applications.filter(app => statusFilter === 'all' || app.status === statusFilter);
  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    contacted: applications.filter(a => a.status === 'contacted').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20" data-id="element-4245" data-mp="26pje4u0g0j4k">
            Pendiente
          </span>;
      case 'contacted':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/10 text-blue-500 border border-blue-500/20" data-id="element-4246" data-mp="2fppqgckend25">
            Contactado
          </span>;
      case 'approved':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" data-id="element-4247" data-mp="2pezg7lrs0jn3">
            Aprobado
          </span>;
      case 'rejected':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-500 border border-red-500/20" data-id="element-4248" data-mp="2zbud608eibz9">
            Rechazado
          </span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-500/10 text-gray-400 border border-gray-500/20" data-id="element-4249" data-mp="1520jjdhcs3jh">
            {status}
          </span>;
    }
  };
  if (guardState !== 'authorized') {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-4250" data-mp="9q0wbxko5vb6">
        <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-4251" data-mp="npifnxf74gd5" />
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-4252" data-mp="iqfmba8kvyl0">
      {/* Idle Warning Banner */}
      <AnimatePresence data-id="element-4253" data-mp="3r68tgyh0rxpe">
        {showIdleWarning && <motion.div initial={{
        y: -60,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -60,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed top-0 left-0 right-0 z-[70] flex items-center justify-center px-4 py-3 bg-amber-500/90 text-[#0B0B0B]" data-id="element-4254" data-mp="3r6s8o7fac7zq">
            <Clock className="w-4 h-4 mr-2 shrink-0" data-id="element-4255" data-mp="1iyvsny0w4et7" />
            <span className="text-sm font-bold" data-id="element-4256" data-mp="1ixazabqbb1wx">
              Por seguridad, tu sesión se cerrará pronto por inactividad.
            </span>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-10" data-id="element-4257" data-mp="19zifovdk583q">
        {/* Header */}
        <div className="flex items-center justify-between gap-4" data-id="element-4258" data-mp="1e02d8021mdqe">
          <div className="flex items-center gap-4" data-id="element-4259" data-mp="21ovdu9tm19rc">
            <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-4260" data-mp="21n0qkouv95k3">
              <Layers className="w-7 h-7 text-[#006847]" data-id="element-4261" data-mp="1sop5f2crqzz1" />
            </div>
            <div data-id="element-4262" data-mp="1sqjf1kilb5nt">
              <h1 className="text-3xl font-black tracking-tight" data-id="element-4263" data-mp="1xni6twes70iu">
                NOIZA Enrollment
              </h1>
              <p className="text-white/40 text-sm mt-1" data-id="element-4264" data-mp="h8qpqfn35fou">
                Gestiona los diferentes tipos de registro y acceso dentro de
                NOIZA
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0" data-id="element-4265" data-mp="2nxsfyfw8eysi">
            {/* Session Info Button */}
            <div className="relative" data-id="element-4266" data-mp="2iy61k34o3ci4">
              <button onClick={() => setShowSessionInfo(!showSessionInfo)} className="flex items-center gap-2 px-3 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-4267" data-mp="2iz7mczbhjdd1">
                <UserIcon className="w-4 h-4" data-id="element-4268" data-mp="254ybwh05cr32" />
                <span className="hidden sm:inline max-w-[160px] truncate" data-id="element-4269" data-mp="207okivt765cl">
                  {sessionEmail || 'Usuario'}
                </span>
              </button>

              {/* Session Info Dropdown */}
              <AnimatePresence data-id="element-4270" data-mp="1r8l3ryhi6219">
                {showSessionInfo && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.95
              }} transition={{
                duration: 0.15
              }} className="absolute right-0 top-full mt-2 w-72 bg-[#141414] border border-white/10 rounded-2xl p-4 shadow-2xl z-50" data-id="element-4271" data-mp="3pne9x72d6juz">
                    <div className="space-y-3" data-id="element-4272" data-mp="tejt9oyibz8c">
                      <div data-id="element-4273" data-mp="xfc3akf7e85o">
                        <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold mb-1" data-id="element-4274" data-mp="xf2l3j0eh5aj">
                          Sesión activa
                        </p>
                        <p className="text-sm font-bold text-white break-all" data-id="element-4275" data-mp="28uraeh14mgei">
                          {sessionEmail || '—'}
                        </p>
                      </div>
                      {sessionUserId && <div data-id="element-4276" data-mp="1pg8lnvb5n8ds">
                          <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold mb-1" data-id="element-4277" data-mp="1pha0mavakf0s">
                            User ID
                          </p>
                          <p className="text-[11px] font-mono text-white/50 break-all" data-id="element-4278" data-mp="1yadkp20fzq4e">
                            {sessionUserId}
                          </p>
                        </div>}
                      <div className="pt-2 border-t border-white/5" data-id="element-4279" data-mp="285yi0mukg27t">
                        <button onClick={handleSignOut} className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl text-sm font-bold transition-colors" data-id="element-4280" data-mp="23982q1ql5ang">
                          <LogOut className="w-4 h-4" data-id="element-4281" data-mp="mtnbrb4mwk73" />
                          Cerrar sesión
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Logout Button */}
            <button onClick={handleSignOut} className="p-2.5 bg-[#141414] border border-white/10 rounded-xl text-white/50 hover:text-red-400 hover:border-red-500/20 transition-colors" title="Cerrar sesión" data-id="element-4282" data-mp="cqi0ukwc6gvz">
              <LogOut className="w-5 h-5" data-id="element-4283" data-mp="3l61pf6m2bb4p" />
            </button>
          </div>
        </div>

        {/* Question */}
        <p className="text-white/50 text-lg font-medium" data-id="element-4284" data-mp="3q5o29exzapcz">
          ¿Qué deseas registrar hoy?
        </p>

        {/* Cards */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-4285" data-mp="3c6opp5nqq0x6">
          {ENROLLMENT_OPTIONS.map(opt => {
          const Icon = opt.icon;
          return <div key={opt.route} className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-4286" data-mp="1adh1t7u2aazo">
                <div className="space-y-4" data-id="element-4287" data-mp="2iw87dhrqnsn9">
                  <div className={`w-12 h-12 ${opt.iconBg} rounded-xl flex items-center justify-center`} data-id="element-4288" data-mp="2ivgbe5u8bswg">
                    <Icon className="w-6 h-6" data-id="element-4289" data-mp="2904xtquk4rqa" />
                  </div>
                  <div data-id="element-4290" data-mp="2dxwypad6jo6m">
                    <h2 className="text-lg font-bold text-white" data-id="element-4291" data-mp="2dz83ndid31zb">
                      {opt.title}
                    </h2>
                    <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-4292" data-mp="31oahsbdsecp2">
                      {opt.description}
                    </p>
                  </div>
                </div>

                <button onClick={() => navigate(opt.route)} className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${opt.btnClass}`} data-id="element-4293" data-mp="2smb1jlaljcn6">
                  {(opt as any).btnLabel || 'Iniciar enrollment'}
                  <ArrowRight className="w-4 h-4" data-id="element-4294" data-mp="1lb2g6izxayvs" />
                </button>
              </div>;
        })}
        </div>

        {/* ═══ COMPACT APPLICATIONS CARD ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-6 hover:border-white/20 transition-colors" data-id="element-4295" data-mp="18pvktnrmtra3">
          <div className="space-y-4" data-id="element-4296" data-mp="1ip356uwsam6p">
            <div className="flex items-start justify-between" data-id="element-4297" data-mp="1iq4k5agx7stp">
              <div className="w-12 h-12 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-4298" data-mp="uz8uojzxt9pm">
                <FileText className="w-6 h-6 text-orange-400" data-id="element-4299" data-mp="q0nugkkoqq16" />
              </div>
              {stats.pending > 0 && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 animate-pulse" data-id="element-4300" data-mp="14pk24gity119">
                  {stats.pending} pendiente{stats.pending !== 1 ? 's' : ''}
                </span>}
            </div>
            <div data-id="element-4301" data-mp="7afv8dk3wjq9">
              <h2 className="text-lg font-bold text-white" data-id="element-4302" data-mp="78v0amtvqyrv">
                Aplicaciones de restaurantes
              </h2>
              <p className="text-sm text-white/40 mt-1 leading-relaxed" data-id="element-4303" data-mp="wxjw6wm0svhk">
                Revisa las solicitudes de ingreso a NOIZA
              </p>
            </div>

            {/* Compact Stats Row */}
            <div className="flex flex-wrap gap-3 pt-2" data-id="element-4304" data-mp="15r5cikdu2kt3">
              <div className="flex items-center gap-1.5" data-id="element-4305" data-mp="1apq27pvglm3a">
                <div className="w-2 h-2 rounded-full bg-white/30" data-id="element-4306" data-mp="1arax5glor71o" />
                <span className="text-xs text-white/50" data-id="element-4307" data-mp="e0io32x66f9e">
                  <span className="font-bold text-white" data-id="element-4308" data-mp="i0j7z331finu">{stats.total}</span>{' '}
                  total
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4309" data-mp="myaxxrk4gupe">
                <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-4310" data-mp="rzi63rq6fvwl" />
                <span className="text-xs text-white/50" data-id="element-4311" data-mp="rwm7s2af357m">
                  <span className="font-bold text-amber-500" data-id="element-4312" data-mp="1ye9bo8q8w4wy">
                    {stats.pending}
                  </span>{' '}
                  pendientes
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4313" data-mp="3nuv4aay4i3gc">
                <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-4314" data-mp="3ixd53c9wr2lj" />
                <span className="text-xs text-white/50" data-id="element-4315" data-mp="3iz6bqrkitmtj">
                  <span className="font-bold text-blue-500" data-id="element-4316" data-mp="3e5bjv0uri1jd">
                    {stats.contacted}
                  </span>{' '}
                  contactados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4317" data-mp="356q39b8uowl8">
                <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-4318" data-mp="306lllhw7hvtf" />
                <span className="text-xs text-white/50" data-id="element-4319" data-mp="305ajrnmbmy4y">
                  <span className="font-bold text-emerald-500" data-id="element-4320" data-mp="2v6pirlz8wafg">
                    {stats.approved}
                  </span>{' '}
                  aprobados
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-4321" data-mp="tge448mv6vlp">
                <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-4322" data-mp="tdru82ci440b" />
                <span className="text-xs text-white/50" data-id="element-4323" data-mp="ohkoa9k3usid">
                  <span className="font-bold text-red-500" data-id="element-4324" data-mp="wh7gs8za1dxa">
                    {stats.rejected}
                  </span>{' '}
                  rechazados
                </span>
              </div>
            </div>
          </div>

          <button onClick={() => setAppsModalOpen(true)} className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors bg-orange-500/20 text-orange-400 hover:bg-orange-500/30" data-id="element-4325" data-mp="mirmjx4ydojk">
            Ver aplicaciones
            <ArrowRight className="w-4 h-4" data-id="element-4326" data-mp="ij10u7ua9ylw" />
          </button>
        </div>

        {/* ═══ ACCESO RÁPIDO KDS ═══ */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors" data-id="element-4327" data-mp="3umqzcyg99vu">
          <div className="space-y-4" data-id="element-4328" data-mp="8q22hy8zitp3">
            <div className="flex items-start justify-between" data-id="element-4329" data-mp="3qx9d25ci0crw">
              <div className="flex items-center gap-3" data-id="element-4330" data-mp="3qzvs0q1pxuk5">
                <div className="w-12 h-12 bg-cyan-500/15 rounded-xl flex items-center justify-center" data-id="element-4331" data-mp="3uybdgpi5iuvg">
                  <ChefHat className="w-6 h-6 text-cyan-400" data-id="element-4332" data-mp="3cbvgk13vb3bk" />
                </div>
                <div data-id="element-4333" data-mp="37crb9lk2dpqi">
                  <h2 className="text-lg font-bold text-white" data-id="element-4334" data-mp="37dk7vtpb6yux">
                    Acceso rápido KDS
                  </h2>
                  <p className="text-sm text-white/40 mt-0.5" data-id="element-4335" data-mp="3l9ny7bx2an24">
                    Ir directo a la cocina de cualquier restaurante
                  </p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20" data-id="element-4336" data-mp="3v7ty5nzb5j4c">
                {restaurants.length} activos
              </span>
            </div>

            {restLoading ? <div className="flex items-center justify-center py-6" data-id="element-4337" data-mp="3ur05xj7p48o">
                <Loader2 className="w-6 h-6 text-white/20 animate-spin" data-id="element-4338" data-mp="hv1p2v0yo8r7" />
              </div> : restaurants.length === 0 ? <p className="text-white/30 text-sm text-center py-4" data-id="element-4339" data-mp="cwqhys21wcco">
                No hay restaurantes activos
              </p> : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 pt-2" data-id="element-4340" data-mp="36lu8tjsas04r">
                {restaurants.map(r => {
              const isHidden = r.show_in_marketplace === false;
              const isToggling = togglingVisibilityId === r.id;
              return <div key={r.id} className="relative flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-xl hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-colors group" data-id="element-4341" data-mp="3kgmt0vj4u4ho">
                      <button onClick={() =>
                // Emilius Pizza no usa el KDS como pantalla de
                // entrada: su operación arranca en Pedidos Web,
                // igual que al iniciar sesión. El resto de
                // restaurantes sigue entrando al KDS.
                navigate(r.slug === 'emilius-pizza' ? `/admin/${r.slug}/pedidos-web` : `/admin/${r.slug}/kds`)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-id="element-4342" data-mp="2arykhq5wqzf">
                        <div className="w-10 h-10 rounded-lg bg-white/10 overflow-hidden shrink-0 flex items-center justify-center" data-id="element-4343" data-mp="1mtkm5u8h41k4">
                          {r.logo_url ? <img src={r.logo_url} alt={r.name} className="w-full h-full object-cover" data-id="element-4344" data-mp="2fhq82kb9jwlj" /> : <Store className="w-5 h-5 text-white/30" data-id="element-4345" data-mp="z26uzrm26c0q" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-4346" data-mp="v0bswkwazulw">
                          <div className="font-bold text-white text-sm truncate group-hover:text-cyan-300 transition-colors flex items-center gap-1.5" data-id="element-4347" data-mp="v2y1mnuxkg5p">
                            {r.name}
                            {isHidden && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/20 shrink-0" data-id="element-4348" data-mp="2l56rinc69yje">
                                OCULTO
                              </span>}
                          </div>
                          <div className="text-xs text-white/30 truncate" data-id="element-4349" data-mp="3hvflds2fag1c">
                            {r.slug}
                          </div>
                        </div>
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  toggleMarketplaceVisibility(r.id, r.show_in_marketplace !== false);
                }} disabled={isToggling} title={isHidden ? 'Mostrar en el marketplace' : 'Ocultar del marketplace'} aria-label={isHidden ? 'Mostrar en el marketplace' : 'Ocultar del marketplace'} className={`p-2 rounded-lg border transition-colors shrink-0 ${isHidden ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'} ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-4350" data-mp="3922oyg55gwai">
                        {isToggling ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-4351" data-mp="1h8mhsnl9e6rr" /> : isHidden ? <EyeOff className="w-4 h-4" data-id="element-4352" data-mp="318dj0t3evd2e" /> : <Eye className="w-4 h-4" data-id="element-4353" data-mp="3160x64uj8ik2" />}
                      </button>
                    </div>;
            })}
              </div>}
          </div>
        </div>

        {/* ═══ DOCUMENTOS LEGALES ═══ */}
        <div className="pt-4 border-t border-white/10" data-id="element-4354" data-mp="2nagnm00p1cpb">
          <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-4355" data-mp="3jzem9tbkdurr">
            Documentos legales
          </p>
          <div className="flex flex-wrap gap-3" data-id="element-4356" data-mp="3fxtbqemi5skq">
            <a href="/terminos-restaurantes" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-4357" data-mp="3b6b97m4tzjxn">
              <FileText className="w-4 h-4 text-[#006847]" data-id="element-4358" data-mp="2p6r9e4j1csn" />
              T&C Restaurantes
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-4359" data-mp="a37iez7yg0d1" />
            </a>
            <a href="/terminos-comensales" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:border-white/20 transition-colors" data-id="element-4360" data-mp="f22203dqxsy7">
              <FileText className="w-4 h-4 text-blue-400" data-id="element-4361" data-mp="syxp2zqu1kx9" />
              T&C Comensales
              <ArrowRight className="w-3.5 h-3.5 text-white/30" data-id="element-4362" data-mp="xxs5jv1buxe7" />
            </a>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ═══ FULL-SCREEN APPLICATIONS MANAGER MODAL ═══ */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence data-id="element-4363" data-mp="1gq31f4xkdv2t">
        {appsModalOpen && <motion.div className="fixed inset-0 z-50 flex items-stretch justify-end" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-4364" data-mp="1gqm9ltxex53n">
            {/* Backdrop */}
            <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAppsModalOpen(false)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4365" data-mp="3hr9bj4e6u8ns" />

            {/* Slide-over Panel */}
            <motion.div className="relative w-full sm:w-[640px] lg:w-[780px] h-full bg-[#0B0B0B] border-l border-white/10 shadow-2xl flex flex-col overflow-hidden" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} data-id="element-4366" data-mp="4bl4in3zu9m7">
              {/* Panel Header */}
              <div className="shrink-0 px-6 py-5 border-b border-white/10 bg-[#0B0B0B]" data-id="element-4367" data-mp="gv8exefoohzs">
                <div className="flex items-center justify-between" data-id="element-4368" data-mp="2vh39kcn3doy">
                  <div className="flex items-center gap-3" data-id="element-4369" data-mp="7vbu5nwf03a0">
                    <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center" data-id="element-4370" data-mp="7wwobcethk7c">
                      <FileText className="w-5 h-5 text-orange-400" data-id="element-4371" data-mp="3q3b2ncgzzw69" />
                    </div>
                    <div data-id="element-4372" data-mp="3u33ato2hc0b3">
                      <h2 className="text-xl font-bold text-white" data-id="element-4373" data-mp="1o9v8i5lpbz6j">
                        Aplicaciones de restaurantes
                      </h2>
                      <p className="text-white/40 text-xs mt-0.5" data-id="element-4374" data-mp="1sa5hlr4sj5pm">
                        Centro de control de solicitudes
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setAppsModalOpen(false)} className="p-2 -mr-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition-colors" data-id="element-4375" data-mp="yn0wd6t3jojc">
                    <X className="w-6 h-6" data-id="element-4376" data-mp="opmj1g2gzfv9" />
                  </button>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-5 gap-3 mt-5" data-id="element-4377" data-mp="fvhw37qgiol0">
                  <div className="bg-[#141414] border border-white/10 rounded-xl p-3 text-center" data-id="element-4378" data-mp="ftx2plfvpboq">
                    <span className="text-white/40 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4379" data-mp="awep3hqx28ae">
                      Total
                    </span>
                    <span className="text-xl font-black text-white" data-id="element-4380" data-mp="ww91ce4n6nm">
                      {stats.total}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-amber-500/20 rounded-xl p-3 text-center" data-id="element-4381" data-mp="28jpm3cmpld13">
                    <span className="text-amber-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4382" data-mp="2clat4hckax3d">
                      Pendientes
                    </span>
                    <span className="text-xl font-black text-amber-500" data-id="element-4383" data-mp="2hiaysr5i1xty">
                      {stats.pending}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-blue-500/20 rounded-xl p-3 text-center" data-id="element-4384" data-mp="1owrku1tiikba">
                    <span className="text-blue-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4385" data-mp="1tvl0o0w9unem">
                      Contactados
                    </span>
                    <span className="text-xl font-black text-blue-500" data-id="element-4386" data-mp="23l4h6zwii56b">
                      {stats.contacted}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-emerald-500/20 rounded-xl p-3 text-center" data-id="element-4387" data-mp="163e9dg5el6m2">
                    <span className="text-emerald-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4388" data-mp="1b1z1steeylzy">
                      Aprobados
                    </span>
                    <span className="text-xl font-black text-emerald-500" data-id="element-4389" data-mp="z1sbdo6ugsxu">
                      {stats.approved}
                    </span>
                  </div>
                  <div className="bg-[#141414] border border-red-500/20 rounded-xl p-3 text-center" data-id="element-4390" data-mp="17vdnezrmjw3k">
                    <span className="text-red-500/60 text-[10px] font-bold uppercase tracking-wider block mb-0.5" data-id="element-4391" data-mp="k5t24a800mr4">
                      Rechazados
                    </span>
                    <span className="text-xl font-black text-red-500" data-id="element-4392" data-mp="u3gz76t2u6cg">
                      {stats.rejected}
                    </span>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex overflow-x-auto hide-scrollbar gap-2 mt-4 pb-1" data-id="element-4393" data-mp="25h1ldp3qamh7">
                  {[{
                id: 'all',
                label: 'Todos'
              }, {
                id: 'pending',
                label: 'Pendientes'
              }, {
                id: 'contacted',
                label: 'Contactados'
              }, {
                id: 'approved',
                label: 'Aprobados'
              }, {
                id: 'rejected',
                label: 'Rechazados'
              }].map(tab => <button key={tab.id} onClick={() => setStatusFilter(tab.id as any)} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${statusFilter === tab.id ? 'bg-white text-[#0B0B0B]' : 'bg-[#141414] text-white/60 border border-white/10 hover:bg-white/5 hover:text-white'}`} data-id="element-4394" data-mp="3seu10vlo06ra">
                      {tab.label}
                    </button>)}
                </div>
              </div>

              {/* Panel Content — Scrollable */}
              <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-4395" data-mp="2kzf95y82qrin">
                {appsLoading ? <div className="space-y-4" data-id="element-4396" data-mp="2fzsv5mkf978s">
                    {[1, 2, 3].map(i => <div key={i} className="h-24 bg-[#141414] rounded-2xl border border-white/5 animate-pulse" data-id="element-4397" data-mp="2b0zj7yks9oaq" />)}
                  </div> : appsError ? <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center" data-id="element-4398" data-mp="2yrby5teg4lat">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" data-id="element-4399" data-mp="2uq9wz70yxz67" />
                    <h3 className="text-lg font-bold text-white mb-1" data-id="element-4400" data-mp="1nej8qp94o8xx">
                      Error al cargar aplicaciones
                    </h3>
                    <p className="text-white/60 text-sm mb-4" data-id="element-4401" data-mp="1jdz99f130t8m">{appsError}</p>
                    <button onClick={fetchApplications} className="px-6 py-2 bg-red-500/20 text-red-400 rounded-xl font-bold text-sm hover:bg-red-500/30 transition-colors" data-id="element-4402" data-mp="3v0t499vz1sxw">
                      Reintentar
                    </button>
                  </div> : filteredApps.length === 0 ? <div className="bg-[#141414] border border-white/10 rounded-2xl p-12 text-center" data-id="element-4403" data-mp="hja975f7llo0">
                    <Inbox className="w-12 h-12 text-white/20 mx-auto mb-4" data-id="element-4404" data-mp="midg6ver21ym" />
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-4405" data-mp="qki7voln0oim">
                      Aún no hay aplicaciones
                    </h3>
                    <p className="text-white/50 text-sm" data-id="element-4406" data-mp="vjl2ef3zrccc">
                      Cuando un restaurante aplique a NOIZA, aparecerá aquí.
                    </p>
                  </div> : <div className="space-y-4" data-id="element-4407" data-mp="2xbq1cajwr9wk">
                    {filteredApps.map(app => <div key={app.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-colors relative overflow-hidden" data-id="element-4408" data-mp="390u82h33ver1">
                        {updatingId === app.id && <div className="absolute inset-0 bg-[#141414]/80 backdrop-blur-sm z-10 flex items-center justify-center" data-id="element-4409" data-mp="3ny4fnfcmo0hh">
                            <Loader2 className="w-6 h-6 text-white animate-spin" data-id="element-4410" data-mp="2v9yrsjqa1vdf" />
                          </div>}

                        <div className="flex flex-col gap-4" data-id="element-4411" data-mp="3080my30z9l59">
                          {/* Row 1: Name + Status + Date */}
                          <div className="flex items-start justify-between gap-3" data-id="element-4412" data-mp="351biv9494hie">
                            <div data-id="element-4413" data-mp="27jl8pipo76uz">
                              <div className="flex items-center gap-2 mb-1" data-id="element-4414" data-mp="27ia2zdcnzp18">
                                <h3 className="font-bold text-lg text-white" data-id="element-4415" data-mp="2chwjpwrpbnes">
                                  {app.restaurant_name}
                                </h3>
                                {getStatusBadge(app.status)}
                              </div>
                              <p className="text-sm text-white/60" data-id="element-4416" data-mp="kmvhmdhl3cxn">
                                {app.owner_name} • {app.business_type}
                              </p>
                            </div>
                            <p className="text-xs text-white/40 shrink-0" data-id="element-4417" data-mp="aqipvl5ln95j">
                              {new Date(app.created_at).toLocaleDateString('es-CR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                            </p>
                          </div>

                          {/* Row 2: Quick Info */}
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-id="element-4418" data-mp="39hohdup1gyg2">
                            <div className="flex items-center gap-2" data-id="element-4419" data-mp="39jrd8vcvhlsp">
                              <span className="text-white/40" data-id="element-4420" data-mp="3eicgz4rc2nlw">WhatsApp:</span>
                              <span className="text-white font-medium" data-id="element-4421" data-mp="30i1pbzi794zo">
                                {app.whatsapp}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-4422" data-mp="3s7we9v25asnt">
                              <span className="text-white/40" data-id="element-4423" data-mp="11vam7kif1c7">
                                Menú digital:
                              </span>
                              <span className="text-white font-medium" data-id="element-4424" data-mp="3jet1zq38klur">
                                {app.has_digital_menu ? 'Sí' : 'No'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-4425" data-mp="f033o4efdii3">
                              <span className="text-white/40" data-id="element-4426" data-mp="jsmg2y1dm7lb">Pedidos WA:</span>
                              <span className="text-white font-medium" data-id="element-4427" data-mp="24ii5ntoawfd6">
                                {app.uses_whatsapp_orders ? 'Sí' : 'No'}
                              </span>
                            </div>
                          </div>

                          {/* Row 3: Actions */}
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5" data-id="element-4428" data-mp="299rrej6qayjs">
                            <button onClick={() => {
                      setSelectedApp(app);
                      setDetailOpen(true);
                    }} className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-1.5" data-id="element-4429" data-mp="1ljdx0m0o8nm1">
                              <Eye className="w-4 h-4" data-id="element-4430" data-mp="36wh0fczumbf5" /> Ver detalle
                            </button>
                            <button onClick={() => handleWhatsApp(app.whatsapp)} disabled={!app.whatsapp} className="px-3 py-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] text-sm font-bold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-1.5" data-id="element-4431" data-mp="31x43u0j6dpzd">
                              <MessageCircle className="w-4 h-4" data-id="element-4432" data-mp="1ph33scy0zdsl" /> WhatsApp
                            </button>

                            <div className="flex-1" data-id="element-4433" data-mp="1uf4nn039zh54" />

                            {app.status === 'pending' && <button onClick={() => updateStatus(app.id, 'contacted')} className="px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-sm font-bold rounded-xl transition-colors" data-id="element-4434" data-mp="1zd673m4m5ih4">
                                Contactado
                              </button>}
                            {app.status !== 'approved' && <button onClick={() => updateStatus(app.id, 'approved')} className="px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-sm font-bold rounded-xl transition-colors" data-id="element-4435" data-mp="2nalyk6er89n7">
                                Aprobar
                              </button>}
                            {app.status !== 'rejected' && <button onClick={() => updateStatus(app.id, 'rejected')} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-bold rounded-xl transition-colors" data-id="element-4436" data-mp="3636g4rp06sbp">
                                Rechazar
                              </button>}
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* ═══ DETAIL MODAL ═══ */}
      {selectedApp && <Modal isOpen={detailOpen} onClose={() => setDetailOpen(false)} title="Detalle de Aplicación" data-id="element-4437" data-mp="1ib5wggp27he3">
          <div className="space-y-6" data-id="element-4438" data-mp="14av6rgzhk8ui">
            <div className="flex items-center justify-between border-b border-white/10 pb-4" data-id="element-4439" data-mp="14e0tipkn6yn2">
              <div data-id="element-4440" data-mp="198ej2bcmibjt">
                <h3 className="text-2xl font-bold text-white" data-id="element-4441" data-mp="udqh376flv8y">
                  {selectedApp.restaurant_name}
                </h3>
                <p className="text-white/60" data-id="element-4442" data-mp="zdmnte6ik0s7">{selectedApp.owner_name}</p>
              </div>
              {getStatusBadge(selectedApp.status)}
            </div>

            <div className="grid grid-cols-2 gap-6" data-id="element-4443" data-mp="2tqcezo5hyvvw">
              <div data-id="element-4444" data-mp="2tq2olzgjimpo">
                <p className="text-white/40 text-sm mb-1" data-id="element-4445" data-mp="2toi28x4dqa2w">WhatsApp</p>
                <p className="text-white font-medium" data-id="element-4446" data-mp="i40pzhn2norq">{selectedApp.whatsapp}</p>
              </div>
              <div data-id="element-4447" data-mp="d5h8pujfb15p">
                <p className="text-white/40 text-sm mb-1" data-id="element-4448" data-mp="d2322a3q85sg">Instagram/Facebook</p>
                <p className="text-white font-medium" data-id="element-4449" data-mp="run231vkxx5o">
                  {selectedApp.social_handle || '-'}
                </p>
              </div>
              <div data-id="element-4450" data-mp="10tqp2gxvawpg">
                <p className="text-white/40 text-sm mb-1" data-id="element-4451" data-mp="10sz2fvm909bb">Tipo de negocio</p>
                <p className="text-white font-medium" data-id="element-4452" data-mp="ws5betdieee7">
                  {selectedApp.business_type}
                </p>
              </div>
              <div data-id="element-4453" data-mp="1aqljoor0scyg">
                <p className="text-white/40 text-sm mb-1" data-id="element-4454" data-mp="1ar5206kl13d0">Fecha de solicitud</p>
                <p className="text-white font-medium" data-id="element-4455" data-mp="15u4wpxvk44my">
                  {new Date(selectedApp.created_at).toLocaleString('es-CR')}
                </p>
              </div>
              <div data-id="element-4456" data-mp="37kq1qzww9smx">
                <p className="text-white/40 text-sm mb-1" data-id="element-4457" data-mp="37l87qo9czl81">
                  ¿Usa pedidos por WhatsApp?
                </p>
                <p className="text-white font-medium" data-id="element-4458" data-mp="2lqr5ja0j4kyh">
                  {selectedApp.uses_whatsapp_orders ? 'Sí' : 'No'}
                </p>
              </div>
              <div data-id="element-4459" data-mp="2vgkhic4l3239">
                <p className="text-white/40 text-sm mb-1" data-id="element-4460" data-mp="2vi5a3w7c8ayh">
                  ¿Tiene menú digital?
                </p>
                <p className="text-white font-medium" data-id="element-4461" data-mp="30f5g6746tdpl">
                  {selectedApp.has_digital_menu ? 'Sí' : 'No'}
                </p>
              </div>
            </div>

            <div data-id="element-4462" data-mp="27s181t9ssj9l">
              <p className="text-white/40 text-sm mb-1" data-id="element-4463" data-mp="27ri1f8pllhav">Objetivo principal</p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-id="element-4464" data-mp="2cpjjbqbafakr">
                <p className="text-white/90" data-id="element-4465" data-mp="2hppf9iolbj8n">
                  {selectedApp.main_goal || 'No especificado'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10" data-id="element-4466" data-mp="3t4ksd07l9cn3">
              <button onClick={() => handleWhatsApp(selectedApp.whatsapp)} className="flex-1 py-3 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors" data-id="element-4467" data-mp="3t6p6sc345u1m">
                <MessageCircle className="w-5 h-5" data-id="element-4468" data-mp="17pv17zu6aww8" /> WhatsApp
              </button>

              {selectedApp.status === 'pending' && <button onClick={() => updateStatus(selectedApp.id, 'contacted')} className="flex-1 py-3 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-xl font-bold transition-colors" data-id="element-4469" data-mp="xt9y5k1prvv1">
                  Marcar Contactado
                </button>}
              {selectedApp.status !== 'approved' && <button onClick={() => updateStatus(selectedApp.id, 'approved')} className="flex-1 py-3 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-xl font-bold transition-colors" data-id="element-4470" data-mp="jvldodbk6qqi">
                  Aprobar
                </button>}
              {selectedApp.status !== 'rejected' && <button onClick={() => updateStatus(selectedApp.id, 'rejected')} className="flex-1 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl font-bold transition-colors" data-id="element-4471" data-mp="2zhszcmy5xjt4">
                  Rechazar
                </button>}
            </div>
          </div>
        </Modal>}

      {/* Toast Notification */}
      {toast && <div className="fixed bottom-6 right-6 z-[60] animate-in slide-in-from-bottom-5 fade-in duration-300" data-id="element-4472" data-mp="2glkvnnkstgzx">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border ${toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'} backdrop-blur-md`} data-id="element-4473" data-mp="2qhntih8math2">
            {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5" data-id="element-4474" data-mp="db7sqard7ujr" /> : <AlertCircle className="w-5 h-5" data-id="element-4475" data-mp="dd0zdq1zaerr" />}
            <span className="font-bold text-sm" data-id="element-4476" data-mp="iasoydf5hoss">{toast.message}</span>
          </div>
        </div>}
    </div>;
}